import { AlertTriangle } from 'lucide-react';

import { t, type UiLangCode } from '../lib/i18n';
import type { Cents, Receipt } from '../lib/types';

/**
 * Inline alert that surfaces a totals-math mismatch between item totals,
 * tax, tip, and the receipt's stamped total. The validator already emits
 * a `totals_mismatch` finding for this case, but the finding's message
 * is technical (untranslated, integer-cents). This component recomputes
 * the gap from the latest receipt fields and renders it actionably.
 *
 * Recomputing on every render is intentional — the user edits item
 * totals from the table above; the alert needs to update live, not stay
 * pinned to the original parse-time math.
 *
 * Tax-inclusive vs tax-exclusive: receipts in EU layouts often print
 * IVA / MwSt. as informational alongside an items-already-include-tax
 * total. The validator accepts either match within tolerance; this
 * component picks whichever convention has the smaller absolute gap so
 * the alert reflects the actual discrepancy, not a bookkeeping
 * convention mismatch.
 */

interface ValidationHintsProps {
  receipt: Receipt;
  lang: UiLangCode;
}

const TOLERANCE_CENTS = 1;

interface GapResult {
  /** Signed; positive means stamped total is greater than the recomputed sum. */
  gap: Cents;
  /** Sum of items + tax + tip (or items + tip when tax-inclusive wins). */
  expected: Cents;
}

function totalsGap(receipt: Receipt): GapResult | null {
  if (receipt.items.length === 0) return null;
  const itemsSum = receipt.items.reduce((s, i) => s + i.total.value, 0);
  const tax: Cents = receipt.tax?.value ?? 0;
  const tip: Cents = receipt.tip?.value ?? 0;
  const total = receipt.total.value;
  const taxExc = itemsSum + tax + tip;
  const taxInc = itemsSum + tip;
  const gapExc = total - taxExc;
  const gapInc = total - taxInc;
  // Tax-inclusive only makes sense when tax was actually parsed; otherwise
  // it collapses to the same number as exclusive.
  const useInclusive = tax > 0 && Math.abs(gapInc) < Math.abs(gapExc);
  const gap = useInclusive ? gapInc : gapExc;
  if (Math.abs(gap) <= TOLERANCE_CENTS) return null;
  return { gap, expected: useInclusive ? taxInc : taxExc };
}

function formatMoney(cents: Cents, currency: string): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

export function ValidationHints({ receipt, lang }: ValidationHintsProps) {
  const result = totalsGap(receipt);
  if (result === null) return null;
  const currency = receipt.currency.value;
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
    >
      <AlertTriangle
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        aria-hidden="true"
      />
      <div className="space-y-1">
        <p className="font-medium">{t(lang, 'validation.totals.title')}</p>
        <p className="text-xs leading-relaxed">
          {t(lang, 'validation.totals.body', {
            actual: formatMoney(receipt.total.value, currency),
            expected: formatMoney(result.expected, currency),
            gap: formatMoney(Math.abs(result.gap), currency),
          })}
        </p>
      </div>
    </div>
  );
}
